import { BankruptcyChecklistResponse } from "../../../api/ClientFileApi";
import { ChecklistStatus, QuestionState } from "../../BankruptcyChecklist.types";
import { computeChecklistStatus } from "../../BankruptcyChecklist.utils";
import { SpecialDebtsAnswerKey, SpecialDebtsFormValues } from "./SpecialDebts.types";

export const EMPTY_SPECIAL_DEBTS: SpecialDebtsFormValues = {
  wasPreviouslySelfEmployed: null,
  isCurrentlySelfEmployed: null,
  hasBusinessLicense: null,
  exercisesTradeAsBusiness: null,
  isBusinessLicenseDormant: null,
  hasMaintenanceArrears: null,
  hasCriminalDebts: null,
  hasCreditDebts: null,
  creditWithinLastFiveYears: null,
  creditRemark: "",
  hasMailOrderDebts: null,
  mailOrderWithinLastThreeYears: null,
  mailOrderRemark: "",
  hasNeverPaidDebts: null,
};

export function mapResponseToSpecialDebts(response: BankruptcyChecklistResponse): SpecialDebtsFormValues {
  return {
    wasPreviouslySelfEmployed: response.wasPreviouslySelfEmployed,
    isCurrentlySelfEmployed: response.isCurrentlySelfEmployed,
    hasBusinessLicense: response.hasBusinessLicense,
    exercisesTradeAsBusiness: response.exercisesTradeAsBusiness,
    isBusinessLicenseDormant: response.isBusinessLicenseDormant,
    hasMaintenanceArrears: response.hasMaintenanceArrears,
    hasCriminalDebts: response.hasCriminalDebts,
    hasCreditDebts: response.hasCreditDebts,
    creditWithinLastFiveYears: response.creditWithinLastFiveYears,
    creditRemark: response.creditRemark ?? "",
    hasMailOrderDebts: response.hasMailOrderDebts,
    mailOrderWithinLastThreeYears: response.mailOrderWithinLastThreeYears,
    mailOrderRemark: response.mailOrderRemark ?? "",
    hasNeverPaidDebts: response.hasNeverPaidDebts,
  };
}

export type SpecialDebtsQuestionStates = Record<SpecialDebtsAnswerKey, QuestionState>;

export function getQuestionStates(values: SpecialDebtsFormValues): SpecialDebtsQuestionStates {
  const businessLicenseVisible = values.isCurrentlySelfEmployed === false;
  const tradeVisible = values.isCurrentlySelfEmployed === true;
  const dormantVisible = values.hasBusinessLicense === true || values.exercisesTradeAsBusiness === true;
  const creditTimingVisible = values.hasCreditDebts === true;
  const mailOrderTimingVisible = values.hasMailOrderDebts === true;
  const neverPaidVisible = values.mailOrderWithinLastThreeYears === true;

  return {
    wasPreviouslySelfEmployed: { visible: true, isWarning: false },
    isCurrentlySelfEmployed: { visible: true, isWarning: false },
    hasBusinessLicense: { visible: businessLicenseVisible, isWarning: false },
    exercisesTradeAsBusiness: { visible: tradeVisible, isWarning: false },
    isBusinessLicenseDormant: { visible: dormantVisible, isWarning: false },
    hasMaintenanceArrears: { visible: true, isWarning: false },
    hasCriminalDebts: { visible: true, isWarning: false },
    hasCreditDebts: { visible: true, isWarning: false },
    creditWithinLastFiveYears: {
      visible: creditTimingVisible,
      isWarning: values.creditWithinLastFiveYears === true,
    },
    hasMailOrderDebts: { visible: true, isWarning: false },
    mailOrderWithinLastThreeYears: {
      visible: mailOrderTimingVisible,
      isWarning: values.mailOrderWithinLastThreeYears === true,
    },
    hasNeverPaidDebts: { visible: neverPaidVisible, isWarning: values.hasNeverPaidDebts === true },
  };
}

export function getSpecialDebtsStatus(values: SpecialDebtsFormValues): ChecklistStatus {
  return computeChecklistStatus(getQuestionStates(values), values);
}

export function clearUnreachableAnswers(values: SpecialDebtsFormValues): SpecialDebtsFormValues {
  const next = { ...values };

  if (next.isCurrentlySelfEmployed !== false) {
    next.hasBusinessLicense = null;
  }
  if (next.isCurrentlySelfEmployed !== true) {
    next.exercisesTradeAsBusiness = null;
  }
  if (next.hasBusinessLicense !== true && next.exercisesTradeAsBusiness !== true) {
    next.isBusinessLicenseDormant = null;
  }

  if (next.hasCreditDebts !== true) {
    next.creditWithinLastFiveYears = null;
  }
  if (next.creditWithinLastFiveYears !== true) {
    next.creditRemark = "";
  }

  if (next.hasMailOrderDebts !== true) {
    next.mailOrderWithinLastThreeYears = null;
  }
  if (next.mailOrderWithinLastThreeYears !== true) {
    next.mailOrderRemark = "";
    next.hasNeverPaidDebts = null;
  }

  return next;
}

export function applyAnswer(
  values: SpecialDebtsFormValues,
  key: SpecialDebtsAnswerKey,
  value: boolean | null
): SpecialDebtsFormValues {
  return clearUnreachableAnswers({ ...values, [key]: value });
}
